/** @typedef {import('./index.js').default} AccessibleWebWidget */
import {
  TRANSLATIONS,
  SUPPORTED_LANGUAGES,
  RTL_LANGUAGES,
  DEFAULT_LANGUAGE_CODE,
} from "./constants/translations.js";

const cleanText = (value) => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const findBuiltinLanguage = (code) => {
  const needle = cleanText(code).toLowerCase();
  if (!needle) return null;
  return (
    SUPPORTED_LANGUAGES.find(
      (entry) => entry.code.toLowerCase() === needle,
    ) || null
  );
};

// 'zh-tw' and 'zh-TW' are the same language: known codes normalize to the
// casing used by the built-in table so persisted values and page tags match,
// while project supplied codes (e.g. 'dz') keep the casing they arrived with.
const canonicalLanguageCode = (code) => {
  const raw = cleanText(code);
  if (!raw) return "";
  return findBuiltinLanguage(raw)?.code || raw;
};

const cloneBuiltinLanguages = () =>
  SUPPORTED_LANGUAGES.map((entry) => ({ ...entry }));

/** @type {{ [methodName: string]: (this: AccessibleWebWidget, ...args: any[]) => any }} */
export const stateMethods = {

  storageAvailable() {
      try {
        const test = '__test__';
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
      } catch {
        return false;
      }
    },

  fetchCookie(name) {
      const cookieName = name + "=";
      try {
        const match = document.cookie.split(';')
          .map(c => c.trim())
          .find(c => c.startsWith(cookieName));
        if (!match) return "{}";
        return decodeURIComponent(match.substring(cookieName.length)) || "{}";
      } catch (e) {
        console.warn('Error reading cookie:', e);
        return "{}";
      }
    },

  storeCookie(name, value, days) {
      try {
        const d = new Date();
        d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
        const expires = "expires=" + d.toUTCString();
        const isSecure = window.location.protocol === 'https:';
        document.cookie = name + "=" + encodeURIComponent(value) + ";" + expires + ";path=/;SameSite=Strict" + (isSecure ? ";Secure" : "");
      } catch (e) {
        console.warn('Error setting cookie:', e);
      }
    },

  getSavedLanguage() {
      try {
        const config = JSON.parse(this.fetchSavedConfig());
        if (config.lang) return config.lang;
      } catch {
        // Ignore parsing errors
      }
      return null;
    },

  // A language counts as user-selected when picked in the widget's language
  // menu (langUserSelected: true). Auto-saves stamp the flag false so they
  // never outrank the embed config or <html lang>. Pre-1.4.0 configs saved
  // lang with no flag at all, and under the old semantics the saved value
  // always won — so a missing flag also counts as user-selected, otherwise
  // upgrading would switch languages on returning visitors.
  getUserSelectedLanguage() {
      try {
        const config = JSON.parse(this.fetchSavedConfig());
        if (config.lang && config.langUserSelected !== false) return config.lang;
      } catch {
        // Ignore parsing errors
      }
      return null;
    },

  // Resolve a language code against the active list first, then the built-in
  // table, so a code that a project removed from the dropdown still resolves
  // for anything that queries it directly. Matching is case-insensitive.
  getLanguageEntry(code) {
    const needle = cleanText(code).toLowerCase();
    if (!needle) return null;
    const tables = [this.supportedLanguages, SUPPORTED_LANGUAGES];
    for (const table of tables) {
      if (!Array.isArray(table)) continue;
      const match = table.find(
        (entry) => cleanText(entry?.code).toLowerCase() === needle,
      );
      if (match) return match;
    }
    return null;
  },

  // Accepts the shapes a project is likely to have at hand:
  //   ['en', 'es']                                   codes only
  //   'en,es'                                        same, from data-acc-languages
  //   { en: { label: 'English' }, dz: {} }           keyed by code
  //   [{ code: 'dz', label: '…', country: 'Bhutan', countryCode: 'BT' }]
  parseLanguageConfig(value) {
    if (Array.isArray(value)) {
      return value
        .map((item) =>
          typeof item === "string" ? { code: item } : item,
        )
        .filter((item) => item && typeof item === "object");
    }
    if (typeof value === "string") {
      return value
        .split(/[,\s]+/)
        .map((code) => cleanText(code))
        .filter(Boolean)
        .map((code) => ({ code }));
    }
    if (value && typeof value === "object") {
      return Object.entries(value).map(([code, entry]) => ({
        ...(entry && typeof entry === "object" ? entry : {}),
        code,
      }));
    }
    return [];
  },

  // Turn `options.languages` / `options.translations` into the runtime
  // language tables. Always re-derived from the module defaults so calling it
  // again (the data attributes are re-read at start time) is idempotent.
  //
  //   languages: ['en', 'es']                        restrict and reorder
  //   languages: [{ code: 'dz', label: '…', …,       add a language
  //                 translations: { 'Close': '…' } }]
  //   translations: { es: { 'Close': 'Cerrar' } }     override built-in strings
  //   showLanguageSelector: true                     keep a one-language card
  //   showCountryInLanguageLabel: false              'Español', not 'Español (Spain)'
  //
  // Anything missing falls back to the built-in entry, and a config that
  // yields no usable language leaves the full built-in list in place.
  applyLanguageConfig(options = {}) {
    const source = options && typeof options === "object" ? options : {};
    const translations = { ...TRANSLATIONS };
    const showCountryGlobally = source.showCountryInLanguageLabel !== false;

    const mergeDictionary = (code, dictionary) => {
      const key = canonicalLanguageCode(code);
      if (!key || !dictionary || typeof dictionary !== "object") return;
      // Custom dictionaries start from English so partially translated
      // languages read as English rather than as raw source keys.
      translations[key] = {
        ...(TRANSLATIONS[key] || TRANSLATIONS[DEFAULT_LANGUAGE_CODE]),
        ...dictionary,
      };
    };

    Object.entries(
      source.translations && typeof source.translations === "object"
        ? source.translations
        : {},
    ).forEach(([code, dictionary]) => mergeDictionary(code, dictionary));

    const configured = this.parseLanguageConfig(source.languages);
    const resolved = [];
    const seen = new Set();

    configured.forEach((entry) => {
      const code = canonicalLanguageCode(entry.code);
      if (!code || seen.has(code)) return;
      seen.add(code);
      mergeDictionary(code, entry.translations || entry.dictionary);

      const builtin = findBuiltinLanguage(code);
      if (!builtin && !translations[code]) {
        console.warn(
          `AccessibleWebWidget: ignoring language "${code}" because it has no translation dictionary. Pass one as \`translations\` on that entry.`,
        );
        return;
      }

      resolved.push({
        code,
        label: cleanText(entry.label) || builtin?.label || code.toUpperCase(),
        country:
          entry.country === undefined
            ? builtin?.country || ""
            : cleanText(entry.country),
        countryCode:
          entry.countryCode === undefined && entry.flag === undefined
            ? builtin?.countryCode || ""
            : cleanText(entry.countryCode ?? entry.flag).toUpperCase(),
        rtl:
          typeof entry.rtl === "boolean"
            ? entry.rtl
            : builtin?.rtl === true || RTL_LANGUAGES.includes(code),
        showCountry: showCountryGlobally && entry.showCountry !== false,
      });
    });

    if (configured.length && !resolved.length) {
      console.warn(
        "AccessibleWebWidget: no usable entry in `languages`; falling back to all built-in languages.",
      );
    }

    this.supportedLanguages = resolved.length ? resolved : cloneBuiltinLanguages();
    this.translations = translations;
    this.showLanguageSelector =
      source.showLanguageSelector === true
        ? true
        : source.showLanguageSelector === false
          ? false
          : this.supportedLanguages.length > 1;
    return this.supportedLanguages;
  },

  getBrowserLanguage() {
      if (typeof navigator === 'undefined') return DEFAULT_LANGUAGE_CODE;
  
      const supportedCodes = this.supportedLanguages.map(lang => lang.code);
  
      // Get browser languages (navigator.languages is an array, navigator.language is a string)
      const browserLanguages = navigator.languages
        ? [...navigator.languages]
        : [navigator.language || navigator.userLanguage || DEFAULT_LANGUAGE_CODE];
  
      for (const browserLang of browserLanguages) {
        // Extract the primary language code (e.g., 'en-US' -> 'en')
        const primaryCode = browserLang.split('-')[0].toLowerCase();
  
        if (supportedCodes.includes(primaryCode)) {
          return primaryCode;
        }
      }
  
      // Nothing the browser asks for is on offer (possible once a project
      // restricts `languages`): fall back to the first language the project
      // did configure, then to English.
      return supportedCodes[0] || DEFAULT_LANGUAGE_CODE;
    },

  getDefaultLanguage() {
      // Priority: 1. Saved language, 2. Browser language, 3. English
      const savedLang = this.getSavedLanguage();
      if (savedLang) {
        const supportedCodes = this.supportedLanguages.map(lang => lang.code);
        if (supportedCodes.includes(savedLang)) {
          return savedLang;
        }
      }
      return this.getBrowserLanguage();
    },

  // Map a configured language tag to a supported dictionary code: exact
  // match first, then the primary subtag ('pt-BR' → 'pt'). 'auto', codes a
  // project removed via `options.languages`, and unsupported tags fall back to
  // the saved/browser language.
  resolveSupportedLanguage(code) {
      const supportedCodes = this.supportedLanguages.map(lang => lang.code);
      const raw = String(code || '').trim();
      if (raw && raw.toLowerCase() !== 'auto') {
        if (supportedCodes.includes(raw)) return raw;
        const match = supportedCodes.find(
          supportedCode => supportedCode.toLowerCase() === raw.toLowerCase()
        );
        if (match) return match;
        const primary = raw.split(/[_-]/)[0].toLowerCase();
        if (supportedCodes.includes(primary)) return primary;
      }
      return this.getDefaultLanguage() || supportedCodes[0] || DEFAULT_LANGUAGE_CODE;
    },

  isDevMode() {
      if (typeof window === 'undefined') return false;
      try {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('acc-dev') === 'true';
      } catch {
        return false;
      }
    },

  getDataAttributeOptions() {
      const options = {};
      if (typeof document === 'undefined') return options;
  
      const attributes = ['lang', 'languages', 'position', 'offset', 'size', 'icon', 'nonce', 'hide-button'];
  
      const assignValue = (key, value) => {
        if (value === null || typeof value === 'undefined' || value === '') return;
        switch (key) {
          case 'lang': {
            const lang = String(value).trim();
            if (lang) options.lang = lang;
            break;
          }
          case 'languages': {
            // Comma (or space) separated language codes, e.g.
            // data-acc-languages="en, es, pt". applyLanguageConfig() parses it.
            const languages = String(value).trim();
            if (languages) options.languages = languages;
            break;
          }
          case 'position': {
            const position = String(value).trim();
            if (position) options.position = position;
            break;
          }
          case 'offset': {
            const normalized = this.normalizeOffset(value);
            if (normalized) options.offset = normalized;
            break;
          }
          case 'size': {
            const normalizedSize = this.normalizeButtonSize(value);
            if (normalizedSize) options.size = normalizedSize;
            break;
          }
          case 'icon': {
            const icon = String(value).trim();
            if (icon) options.icon = icon;
            break;
          }
          case 'nonce': {
            const nonce = String(value).trim();
            if (nonce) options.nonce = nonce;
            break;
          }
          case 'hide-button': {
            options.hideButton = String(value).trim().toLowerCase() !== 'false';
            break;
          }
          default:
            break;
        }
      };
  
      const inspectElement = (el) => {
        if (!el) return;
        attributes.forEach(key => {
          const attrName = `data-acc-${key}`;
          const attrValue = el.getAttribute ? el.getAttribute(attrName) : null;
          assignValue(key, attrValue);
        });
      };
  
      const scriptCandidates = [];
      if (document.currentScript) {
        scriptCandidates.push(document.currentScript);
      }
      document.querySelectorAll('script[data-acc-lang],script[data-acc-languages],script[data-acc-position],script[data-acc-offset],script[data-acc-size],script[data-acc-icon],script[data-acc-nonce],script[data-acc-hide-button]').forEach(script => {
        if (!scriptCandidates.includes(script)) {
          scriptCandidates.push(script);
        }
      });
      scriptCandidates.forEach(inspectElement);
  
      attributes.forEach(key => {
        if (options[key] !== undefined) return;
        const el = document.querySelector(`[data-acc-${key}]`);
        if (el) {
          assignValue(key, el.getAttribute(`data-acc-${key}`));
        }
      });
  
      return options;
    },

  normalizeOffset(value) {
      if (!value && value !== 0) return undefined;
      let parts = [];
      if (Array.isArray(value)) {
        parts = value;
      } else if (typeof value === 'string') {
        parts = value.split(/[, ]+/);
      } else {
        parts = [value];
      }
      const parsed = parts
        .map(part => {
          const number = Number(part);
          return Number.isFinite(number) ? Math.round(number) : null;
        })
        .filter(number => number !== null);
  
      if (!parsed.length) return undefined;
      if (parsed.length === 1) parsed.push(parsed[0]);
      return [parsed[0], parsed[1] !== undefined ? parsed[1] : parsed[0]];
    },

  normalizeButtonSize(value) {
      const fallback = this.widgetTheme?.buttonSize || '48px';
      if (typeof value === 'number' && Number.isFinite(value)) {
        return `${Math.max(24, Math.round(value))}px`;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return fallback;
        if (/^\d+(\.\d+)?(px|em|rem|%)$/i.test(trimmed)) {
          return trimmed;
        }
        const numeric = Number(trimmed);
        if (Number.isFinite(numeric)) {
          return `${Math.max(24, Math.round(numeric))}px`;
        }
        return trimmed;
      }
      return fallback;
    },


  isSystemControlledPreference(key) {
      const systemDefaults = this.widgetConfig?.systemDefaults || {};
      return Object.prototype.hasOwnProperty.call(systemDefaults, key);
    },

  hasExplicitStatePreference(key) {
      const states = this.widgetConfig?.states || {};
      if (!Object.prototype.hasOwnProperty.call(states, key)) {
        return false;
      }
      return !this.isSystemControlledPreference(key);
    },


  updateState(payload, options = {}) {
      const source = options.source || 'user';
      const previousStates = this.widgetConfig.states || {};
      const previousSystemDefaults = this.widgetConfig.systemDefaults || {};
      const nextStates = { ...previousStates, ...payload };
      const nextSystemDefaults = { ...previousSystemDefaults };
      const keys = Object.keys(payload || {});

      if (source === 'system') {
        keys.forEach((key) => {
          nextSystemDefaults[key] = payload[key];
        });
      } else {
        keys.forEach((key) => {
          if (Object.prototype.hasOwnProperty.call(nextSystemDefaults, key)) {
            delete nextSystemDefaults[key];
          }
        });
      }

      const updatedConfig = { ...this.widgetConfig, states: nextStates, systemDefaults: nextSystemDefaults };
      this.saveConfig(updatedConfig);
      return updatedConfig;
    },

  // Return keys to "no preference": unlike writing an explicit false via
  // updateState, this lets system defaults (prefers-reduced-motion,
  // prefers-contrast) re-apply afterwards.
  clearStates(keys) {
      const nextStates = { ...(this.widgetConfig.states || {}) };
      const nextSystemDefaults = { ...(this.widgetConfig.systemDefaults || {}) };
      (keys || []).forEach((key) => {
        delete nextStates[key];
        delete nextSystemDefaults[key];
      });
      const updatedConfig = { ...this.widgetConfig, states: nextStates, systemDefaults: nextSystemDefaults };
      this.saveConfig(updatedConfig);
      return updatedConfig;
    },

  saveConfig(newConfig) {
      this.widgetConfig = { ...this.widgetConfig, ...newConfig };
      if (this.storageAvailable()) {
        try {
          localStorage.setItem(this.cookieKey, JSON.stringify(this.widgetConfig));
        } catch {
          this.storeCookie(this.cookieKey, JSON.stringify(this.widgetConfig), 365);
        }
      } else {
        this.storeCookie(this.cookieKey, JSON.stringify(this.widgetConfig), 365);
      }
    },

  retrieveState(key) {
      return this.widgetConfig.states ? this.widgetConfig.states[key] : undefined;
    },

  loadConfig(cache = true) {
      if (cache) return this.widgetConfig;
      const savedConfig = this.fetchSavedConfig();
      if (savedConfig) {
        try {
          this.widgetConfig = JSON.parse(savedConfig);
        } catch (e) {
          console.warn('Error parsing config:', e);
          this.widgetConfig = {};
        }
      }
      return this.widgetConfig;
    },

  fetchSavedConfig() {
      if (this.storageAvailable()) {
        try {
          const stored = localStorage.getItem(this.cookieKey);
          if (stored) return stored;
        } catch (e) {
          console.warn('localStorage failed, falling back to cookies:', e);
        }
      }
      const cookieVal = this.fetchCookie(this.cookieKey);
      return cookieVal && cookieVal !== "" ? cookieVal : "{}";
    },


};
